import { MESSAGE_SETUP, MESSAGE_STATE, MESSAGE_STATUS, MESSAGE_TYPE } from '@/constants/message';
import store from '@/store';
import Attachment from '@/classes/v2/Attachment';
import { parseGroupMessageList } from '@/lib/SMSNumberParser.js';

/**
 * Message Class.
 *
 * @typedef {Object} Message
 * @typedef {Object} Contact
 *
 * @module Message
 **/
export default class Message {

  /**
   * @constructor
   * @this {Message}
   *
   * @param {Object} RawMessage - Message information.
   **/
  constructor({
    from,
    to,
    toList,
    body,
    attachments,
    originationTime,
    deliveredTime,
    readTime,
    messageType,
    messageState,
    messageStatus,
    hasAttachments,
    providerMessageId,
    rowId,
    groupMessage,
    messageDirection
  }) {
    const requiredParams = [from, body, originationTime];
    if (requiredParams.some(param => param === undefined)) {
      throw new Error('from, to or toList, body, and originationTime are required parameters.');
    }
    if (to === undefined || null) {
      if (toList === undefined || null) {
        throw new Error('from, to or toList, body, and originationTime are required parameters.');
      }
    }

    this.providerMessageId = providerMessageId;

    this.from = from;
    this.to = to || null;
    this.toList = toList || null;
    this.body = body;
    this.groupMessage = groupMessage;
    this.messageDirection = messageDirection;

    this.originationTime = originationTime;
    this.deliveredTime = deliveredTime || null;
    this.readTime = readTime || null;

    this.rowId = rowId;
    this.attachmentsId = hasAttachments ? rowId : null;
    this.attachments = attachments?.map(attachment => new Attachment(attachment)) || null;

    this.messageType = messageType || MESSAGE_TYPE.sms;
    this.stateHistory = messageState ? [messageState] : [];
    this.statusHistory = messageStatus ? [messageStatus] : [];
  }

  /**
   * Public getters
   */

  /**
   * Determines the parent conversation id based on message data.
   *
   * @returns {String}
   */
  get parentConversationId() {
    if (this.isFromCurrentUser) {
      return this.to;
    }

    if (!this.groupMessage) {
      return this.from;
    }

    const currentUser = store.getters['user/currentUser'];
    const toWithoutSelf = this.to.replace(currentUser.number, '');
    const inboundGroupKey = `${this.from}${toWithoutSelf}`;

    return parseGroupMessageList({ to: inboundGroupKey }).sort().join('')
  }

  /**
   * Determines whether current message is from current user or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isFromCurrentUser() {
    const currentUser = store.getters['user/currentUser'];
    return this.from === currentUser.number;
  }

  /**
   * Returns an object containing 3 values reflecting the current status of the message.
   *
   * @returns {Object<{inProcess, processed, error}>}
   */
  get lifecycle() {
    return {
      inProcess: !this.statusHistory.length,
      processed: !!this.statusHistory.length,
      error: this.statusHistory.includes(MESSAGE_STATUS.error)
    };
  }

  /**
   * Public Methods
   */

  /**
   * Determine if the message is missing attachment content.
   * @returns {Boolean}
   */
  isMissingAttachmentContent() {
    if (!this.attachments) return false;

    for (const attachment of this.attachments) {
      if (!attachment.content) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determines whether a given rawMessage is an update for the current instance or not,
   * returning true or false as appropriate.
   *
   * @param {Message} rawMessage - Source message to check.
   * @param {Boolean} allowReadState - Determines whether to allow or ignore to READ updates.
   *
   * @returns {Boolean}
   */
  isMyUpdate({ from, to, body, originationTime, messageState, providerMessageId, attachments }, allowReadState) {
    // This logic should be improved.
    // For now, we dont have a proper way to detect that a message is just an update of another message.
    // This logic is just to infer if a given message is a high likely update of current message instance.
    // When loading conversations from API READ state is always allowed.

    // 0. Provider Message Id match.
    if (this.providerMessageId && this.providerMessageId === providerMessageId) {
      return true;
    } else if(this.providerMessageId && providerMessageId && this.providerMessageId !== providerMessageId){
       return false;
    }

    // 1. An update should have the same info in these 3 fields. [from, to, body].
    const sameContent = [this.from === from, this.to === to, this.body === body];

    // 2. A message update never repeats a previous message state.
    //    EXCEPT when "from === to" in this case we receive twice each event.
    const avoidDuplicatedStates =
      from !== to
        ? !this.stateHistory.includes(messageState)
        : this.stateHistory.filter(state => state === messageState).length <= 1;

    // 3. Update just if the origination time difference is equal or smaller than MAX_DIFF
    //    EXCEPT when no state history, in this scenario the originationTime was added to create an
    //    approximately originationTime so that UI can sort messages correctly.
    const MAX_DIFF = this.stateHistory.length === 0 ? Number.MAX_SAFE_INTEGER : MESSAGE_SETUP.EVENT_MAX_DIFF_TIME_MS;
    const originationTimeSmallGap = Math.abs(this.originationTime - originationTime) <= MAX_DIFF;

    // 4. Ignore READ updates for now. Usually read updates are received after a while.
    const ignoreReadUpdate = messageState !== MESSAGE_STATE.read;

    // 5. If the MMS includes attachments it should match.
    const hasAttachments = attachments && this.attachments;
    const attachmentsContent = attachments?.map(({ content }) => content) || [];
    const matchAttachment = this.attachments?.some(attachment => attachmentsContent.includes(attachment.content));

    const premises = [
      ...sameContent,
      originationTimeSmallGap,
      avoidDuplicatedStates,
      allowReadState ? true : ignoreReadUpdate,
      hasAttachments ? matchAttachment : true
    ];

    return premises.every(premise => premise);
  }

  /**
   * Determines whether one string may be found within comparable Message fields,
   * returning true or false as appropriate.
   *
   * @param {String} searchString
   *
   * @returns {Boolean}
   */
  includes(searchString) {
    const comparableFields = [this.body].join().toUpperCase();
    return comparableFields.includes(searchString.toUpperCase());
  }

  /**
   * Private Methods
   */

  /**
   * Public static methods
   */

  /**
   * Returns a Unicode encoded version of a given string.
   *
   * @param {String} inputString
   *
   * @returns {String}
   */
  static toUnicode(inputString) {
    return inputString.split('').map(value => {
      const temp = value.codePointAt(0).toString(16);
      return temp.length > 2 ? '\\u' + temp : value;
    }).join('');
  }

  /**
   * Compares two given Message objects for order.
   * Returns a negative integer, zero, or a positive integer as MessageA object is less than,
   * equal to, or greater than MessageB object.
   *
   * @param {Message} messageA
   * @param {Message} messageB
   *
   * @returns {Number}
   */
  static compare(messageA, messageB) {
    let dateTimeA = messageA.isFromCurrentUser ? messageA.originationTime : messageA.deliveredTime;
    let dateTimeB = messageB.isFromCurrentUser ? messageB.originationTime : messageB.deliveredTime;

    // fallback datetime
    if (!dateTimeA) dateTimeA = messageA.originationTime;
    if (!dateTimeB) dateTimeB = messageB.originationTime;

    return dateTimeA - dateTimeB;
  }
}
